import { useState, useMemo, useId } from 'react';
import {
  LogIn,
  LogOut,
  Users,
  Search,
  Loader2,
  Calendar,
  UserPlus,
  ArrowRightLeft,
  CalendarPlus,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { format, parseISO, addDays, subDays } from 'date-fns';
import { cn } from '@/lib/utils';
import { PageHeader } from '@/components/ui/PageHeader';
import { Button } from '@/components/ui/Button';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { PaymentTypeBadge } from '@/components/ui/PaymentTypeBadge';
import { EmptyState } from '@/components/ui/EmptyState';
import { SlideOver } from '@/components/ui/SlideOver';
import { CheckInWizard } from '@/components/frontdesk/CheckInWizard';
import { CheckOutWizard } from '@/components/frontdesk/CheckOutWizard';
import { RoomMoveSlideOver } from '@/components/frontdesk/RoomMoveSlideOver';
import { ExtendStayModal } from '@/components/frontdesk/ExtendStayModal';
import { RecentlyViewedPanel } from '@/components/frontdesk/RecentlyViewedPanel';
import { Building } from 'lucide-react';
import { useSelectedPropertyId } from '@/contexts/PropertyContext';
import { useCreateWalkIn } from '@/hooks/api/useBookings';
import { useUnits } from '@/hooks/api/useProperties';
import { useCompanies } from '@/hooks/api/useCompanies';
import { PrintBillButton } from '@/components/ui/PrintBillButton';
import {
  useArrivals,
  useDepartures,
  useInHouse,
  useSearchReservations,
} from '@/hooks/api/useFrontDesk';
import type {
  BookingEntry,
  SearchReservationsQuery,
} from '@/services/api/frontdesk.api';

// ─── Constants ───────────────────────────────────────────────────────────────

type TabKey = 'arrivals' | 'departures' | 'in-house' | 'search';

const TABS: { key: TabKey; label: string; icon: typeof LogIn }[] = [
  { key: 'arrivals', label: 'Arrivals', icon: LogIn },
  { key: 'departures', label: 'Departures', icon: LogOut },
  { key: 'in-house', label: 'In House', icon: Users },
  { key: 'search', label: 'Search', icon: Search },
];

function fmtDate(iso: string): string {
  try {
    return format(parseISO(iso), 'MMM d, yyyy');
  } catch {
    return iso;
  }
}

// ─── Page ────────────────────────────────────────────────────────────────────

export function FrontDeskPage() {
  const [activeTab, setActiveTab] = useState<TabKey>('arrivals');
  const [selectedDate, setSelectedDate] = useState(() => format(new Date(), 'yyyy-MM-dd'));
  const [walkInOpen, setWalkInOpen] = useState(false);
  const [corporateOpen, setCorporateOpen] = useState(false);
  const propertyId = useSelectedPropertyId();

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchStatus, setSearchStatus] = useState('');
  const [searchSource, setSearchSource] = useState('');
  const [searchSubmitted, setSearchSubmitted] = useState(false);
  const searchParams: SearchReservationsQuery = useMemo(() => {
    const term = searchQuery.trim();
    // The single search box accepts a guest name or a booking UUID; the backend
    // exposes those as separate fields, so route by shape.
    const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(term);
    return {
      guestName: !isUuid && term ? term : undefined,
      bookingId: isUuid ? term : undefined,
      propertyId: propertyId || undefined,
      status: searchStatus || undefined,
      source: searchSource || undefined,
    };
  }, [searchQuery, propertyId, searchStatus, searchSource]);

  const arrivalsQuery = useArrivals({ date: selectedDate, propertyId });
  const departuresQuery = useDepartures({ date: selectedDate, propertyId });
  const inHouseQuery = useInHouse({ propertyId });
  const searchResultsQuery = useSearchReservations(searchParams, searchSubmitted && activeTab === 'search');

  // Wizard state
  const [checkInBooking, setCheckInBooking] = useState<BookingEntry | null>(null);
  const [checkOutBooking, setCheckOutBooking] = useState<BookingEntry | null>(null);
  const [moveBooking, setMoveBooking] = useState<BookingEntry | null>(null);
  const [extendBooking, setExtendBooking] = useState<BookingEntry | null>(null);

  return (
    <div>
      <PageHeader
        title="Front Desk"
        description="Manage arrivals, departures, and in-house guests"
        actions={
          <div className="flex items-center gap-3">
            <Button
              variant="primary"
              size="sm"
              onClick={() => setWalkInOpen(true)}
            >
              <UserPlus size={16} aria-hidden="true" />
              Walk-in
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setCorporateOpen(true)}
            >
              <Building size={16} aria-hidden="true" />
              Corporate
            </Button>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setSelectedDate(format(new Date(), 'yyyy-MM-dd'))}
                className={cn(
                  'px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors',
                  selectedDate === format(new Date(), 'yyyy-MM-dd')
                    ? 'bg-primary-600 text-white border-primary-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50',
                )}
              >
                Today
              </button>
              <button
                type="button"
                onClick={() => setSelectedDate(format(addDays(new Date(), 1), 'yyyy-MM-dd'))}
                className={cn(
                  'px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors',
                  selectedDate === format(addDays(new Date(), 1), 'yyyy-MM-dd')
                    ? 'bg-primary-600 text-white border-primary-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50',
                )}
              >
                Tomorrow
              </button>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setSelectedDate(format(subDays(parseISO(selectedDate), 1), 'yyyy-MM-dd'))}
                  className="p-1.5 rounded-lg border border-gray-300 text-gray-500 hover:bg-gray-50 transition-colors"
                  aria-label="Previous day"
                >
                  <ChevronLeft size={16} aria-hidden="true" />
                </button>
                <div className="flex items-center gap-2">
                  <Calendar size={16} className="text-gray-400" aria-hidden="true" />
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    aria-label="Select date"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedDate(format(addDays(parseISO(selectedDate), 1), 'yyyy-MM-dd'))}
                  className="p-1.5 rounded-lg border border-gray-300 text-gray-500 hover:bg-gray-50 transition-colors"
                  aria-label="Next day"
                >
                  <ChevronRight size={16} aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>
        }
      />

      <WalkInModal
        isOpen={walkInOpen}
        onClose={() => setWalkInOpen(false)}
        propertyId={propertyId}
      />

      <CorporateBookingModal
        isOpen={corporateOpen}
        onClose={() => setCorporateOpen(false)}
        propertyId={propertyId}
      />

      {/* Wizards */}
      {checkInBooking && (
        <CheckInWizard
          isOpen={!!checkInBooking}
          onClose={() => setCheckInBooking(null)}
          booking={checkInBooking}
        />
      )}
      {checkOutBooking && (
        <CheckOutWizard
          isOpen={!!checkOutBooking}
          onClose={() => setCheckOutBooking(null)}
          booking={checkOutBooking}
        />
      )}
      {moveBooking && (
        <RoomMoveSlideOver
          isOpen={!!moveBooking}
          onClose={() => setMoveBooking(null)}
          booking={moveBooking}
        />
      )}
      {extendBooking && (
        <ExtendStayModal
          isOpen={!!extendBooking}
          onClose={() => setExtendBooking(null)}
          booking={extendBooking}
        />
      )}

      {/* Recently viewed reservations (client-side, localStorage) */}
      <RecentlyViewedPanel />

      {/* Selected date display */}
      <div className="mb-4 px-1">
        <p className="text-lg font-semibold text-gray-800">
          {(() => {
            try {
              return format(parseISO(selectedDate), 'EEEE, MMMM d, yyyy');
            } catch {
              return selectedDate;
            }
          })()}
        </p>
        {selectedDate === format(new Date(), 'yyyy-MM-dd') && (
          <p className="text-xs text-primary-600 font-medium mt-0.5">Today</p>
        )}
      </div>

      {/* Tab navigation */}
      <div className="border-b border-gray-200 mb-6" role="tablist" aria-label="Front desk views">
        <div className="flex gap-0 -mb-px">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={activeTab === tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={cn(
                  'flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors',
                  activeTab === tab.key
                    ? 'border-primary-600 text-primary-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
                )}
              >
                <Icon size={16} aria-hidden="true" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab content */}
      {activeTab === 'arrivals' && (
        <ArrivalsTab
          data={arrivalsQuery.data}
          isLoading={arrivalsQuery.isLoading}
          onCheckIn={(booking) => setCheckInBooking(booking)}
        />
      )}
      {activeTab === 'departures' && (
        <DeparturesTab
          data={departuresQuery.data}
          isLoading={departuresQuery.isLoading}
          onCheckOut={(booking) => setCheckOutBooking(booking)}
        />
      )}
      {activeTab === 'in-house' && (
        <InHouseTab
          data={
            // fetchInHouse returns BookingEntry[] but guard against the backend
            // returning a paginated { data: BookingEntry[] } envelope
            Array.isArray(inHouseQuery.data)
              ? inHouseQuery.data
              : (inHouseQuery.data as { data?: BookingEntry[] } | undefined)?.data ?? undefined
          }
          isLoading={inHouseQuery.isLoading}
          onMoveRoom={(booking) => setMoveBooking(booking)}
          onExtendStay={(booking) => setExtendBooking(booking)}
        />
      )}
      {activeTab === 'search' && (
        <SearchTab
          searchQuery={searchQuery}
          onSearchQueryChange={setSearchQuery}
          searchStatus={searchStatus}
          onSearchStatusChange={setSearchStatus}
          searchSource={searchSource}
          onSearchSourceChange={setSearchSource}
          onSubmit={() => setSearchSubmitted(true)}
          results={searchResultsQuery.data}
          isLoading={searchResultsQuery.isLoading}
        />
      )}
    </div>
  );
}

// ─── Helper: get guest name from booking entry ──────────────────────────────

function guestName(b: BookingEntry): string {
  return b.guest?.fullName ?? 'Unknown Guest';
}

function unitName(b: BookingEntry): string {
  return b.unit?.name ?? b.unitId.slice(0, 8);
}

// ─── Arrivals Tab ────────────────────────────────────────────────────────────

function ArrivalsTab({
  data,
  isLoading,
  onCheckIn,
}: {
  data: BookingEntry[] | undefined;
  isLoading: boolean;
  onCheckIn: (booking: BookingEntry) => void;
}) {
  if (isLoading) return <LoadingSpinner />;
  const arrivals = data ?? [];

  return (
    <div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <SummaryCard label="Total Arrivals" value={arrivals.length} color="text-blue-600" />
      </div>
      {arrivals.length === 0 ? (
        <EmptyState icon={LogIn} title="No arrivals" description="No arrivals expected for this date." />
      ) : (
        <DataTable>
          <thead>
            <tr>
              <Th>Guest</Th>
              <Th>Unit</Th>
              <Th>Check-in</Th>
              <Th>Check-out</Th>
              <Th>Status</Th>
              <Th>Payment</Th>
              <Th>Amount</Th>
              <Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {arrivals.map((a) => (
              <tr key={a.id} className="hover:bg-gray-50">
                <Td>{guestName(a)}</Td>
                <Td>{unitName(a)}</Td>
                <Td>{fmtDate(a.checkIn)}</Td>
                <Td>{fmtDate(a.checkOut)}</Td>
                <Td><StatusBadge status={a.status} /></Td>
                <Td><PaymentTypeBadge paymentType={a.paymentType} /></Td>
                <Td>${a.totalAmount}</Td>
                <Td>
                  {a.status === 'confirmed' || a.status === 'pending' ? (
                    <Button
                      size="sm"
                      onClick={() => onCheckIn(a)}
                    >
                      Check In
                    </Button>
                  ) : (
                    <span className="text-xs text-gray-400">--</span>
                  )}
                </Td>
              </tr>
            ))}
          </tbody>
        </DataTable>
      )}
    </div>
  );
}

// ─── Departures Tab ──────────────────────────────────────────────────────────

function DeparturesTab({
  data,
  isLoading,
  onCheckOut,
}: {
  data: BookingEntry[] | undefined;
  isLoading: boolean;
  onCheckOut: (booking: BookingEntry) => void;
}) {
  if (isLoading) return <LoadingSpinner />;
  const departures = data ?? [];

  return (
    <div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <SummaryCard label="Total Departures" value={departures.length} color="text-blue-600" />
      </div>
      {departures.length === 0 ? (
        <EmptyState icon={LogOut} title="No departures" description="No departures expected for this date." />
      ) : (
        <DataTable>
          <thead>
            <tr>
              <Th>Guest</Th>
              <Th>Unit</Th>
              <Th>Check-in</Th>
              <Th>Check-out</Th>
              <Th>Status</Th>
              <Th>Amount</Th>
              <Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {departures.map((d) => (
              <tr key={d.id} className="hover:bg-gray-50">
                <Td>{guestName(d)}</Td>
                <Td>{unitName(d)}</Td>
                <Td>{fmtDate(d.checkIn)}</Td>
                <Td>{fmtDate(d.checkOut)}</Td>
                <Td><StatusBadge status={d.status} /></Td>
                <Td>${d.totalAmount}</Td>
                <Td>
                  <div className="flex items-center gap-1">
                    <PrintBillButton
                      bookingId={d.id}
                      variant="inline"
                      label="Bill"
                    />
                    {d.status === 'checked_in' && (
                      <Button
                        size="sm"
                        onClick={() => onCheckOut(d)}
                      >
                        Check Out
                      </Button>
                    )}
                  </div>
                </Td>
              </tr>
            ))}
          </tbody>
        </DataTable>
      )}
    </div>
  );
}

// ─── In House Tab ────────────────────────────────────────────────────────────

function InHouseTab({
  data,
  isLoading,
  onMoveRoom,
  onExtendStay,
}: {
  data: BookingEntry[] | undefined;
  isLoading: boolean;
  onMoveRoom: (booking: BookingEntry) => void;
  onExtendStay: (booking: BookingEntry) => void;
}) {
  if (isLoading) return <LoadingSpinner />;
  const guests = data ?? [];

  return (
    <div>
      <div className="grid grid-cols-2 gap-4 mb-6">
        <SummaryCard label="In House" value={guests.length} color="text-blue-600" />
      </div>
      {guests.length === 0 ? (
        <EmptyState icon={Users} title="No guests in house" description="No guests are currently checked in." />
      ) : (
        <DataTable>
          <thead>
            <tr>
              <Th>Guest</Th>
              <Th>Unit</Th>
              <Th>Check-in</Th>
              <Th>Check-out</Th>
              <Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {guests.map((g) => (
              <tr key={g.id} className="hover:bg-gray-50">
                <Td>{guestName(g)}</Td>
                <Td>{unitName(g)}</Td>
                <Td>{fmtDate(g.checkIn)}</Td>
                <Td>{fmtDate(g.checkOut)}</Td>
                <Td>
                  <div className="flex items-center gap-1">
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => onMoveRoom(g)}
                      aria-label={`Move room for ${guestName(g)}`}
                    >
                      <ArrowRightLeft size={14} aria-hidden="true" />
                      Move
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => onExtendStay(g)}
                      aria-label={`Extend stay for ${guestName(g)}`}
                    >
                      <CalendarPlus size={14} aria-hidden="true" />
                      Extend
                    </Button>
                    <PrintBillButton
                      bookingId={g.id}
                      variant="inline"
                      label="Print Bill"
                    />
                  </div>
                </Td>
              </tr>
            ))}
          </tbody>
        </DataTable>
      )}
    </div>
  );
}

// ─── Search Tab ──────────────────────────────────────────────────────────────

function SearchTab({
  searchQuery,
  onSearchQueryChange,
  searchStatus,
  onSearchStatusChange,
  searchSource,
  onSearchSourceChange,
  onSubmit,
  results,
  isLoading,
}: {
  searchQuery: string;
  onSearchQueryChange: (v: string) => void;
  searchStatus: string;
  onSearchStatusChange: (v: string) => void;
  searchSource: string;
  onSearchSourceChange: (v: string) => void;
  onSubmit: () => void;
  results: { data?: BookingEntry[]; meta?: { total: number } } | undefined;
  isLoading: boolean;
}) {
  const items: BookingEntry[] = results?.data ?? [];

  return (
    <div>
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          <div>
            <label htmlFor="search-q" className="block text-sm font-medium text-gray-700 mb-1">Guest Name / Booking ID</label>
            <input
              id="search-q"
              type="text"
              value={searchQuery}
              onChange={(e) => onSearchQueryChange(e.target.value)}
              placeholder="Search..."
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label htmlFor="search-status" className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              id="search-status"
              value={searchStatus}
              onChange={(e) => onSearchStatusChange(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">All Statuses</option>
              <option value="confirmed">Confirmed</option>
              <option value="pending">Pending</option>
              <option value="checked_in">Checked In</option>
              <option value="checked_out">Checked Out</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <div>
            <label htmlFor="search-source" className="block text-sm font-medium text-gray-700 mb-1">Source</label>
            <select
              id="search-source"
              value={searchSource}
              onChange={(e) => onSearchSourceChange(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">All Sources</option>
              <option value="direct">Direct</option>
              <option value="airbnb">Airbnb</option>
              <option value="booking_com">Booking.com</option>
              <option value="expedia">Expedia</option>
              <option value="axis_rooms">AxisRooms</option>
              <option value="manual">Manual</option>
            </select>
          </div>
          <div className="flex items-end">
            <Button onClick={onSubmit} className="w-full">
              <Search size={16} aria-hidden="true" />
              Search
            </Button>
          </div>
        </div>
      </div>

      {isLoading ? (
        <LoadingSpinner />
      ) : items.length > 0 ? (
        <DataTable>
          <thead>
            <tr>
              <Th>Guest</Th>
              <Th>Unit</Th>
              <Th>Check-in</Th>
              <Th>Check-out</Th>
              <Th>Source</Th>
              <Th>External ID</Th>
              <Th>Status</Th>
              <Th>Amount</Th>
            </tr>
          </thead>
          <tbody>
            {items.map((r) => (
              <tr key={r.id} className="hover:bg-gray-50">
                <Td>{guestName(r)}</Td>
                <Td>{unitName(r)}</Td>
                <Td>{fmtDate(r.checkIn)}</Td>
                <Td>{fmtDate(r.checkOut)}</Td>
                <Td>
                  <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-gray-100 text-gray-700">
                    {r.source}
                  </span>
                </Td>
                <Td><span className="font-mono text-xs text-gray-500">{r.externalBookingId ?? '—'}</span></Td>
                <Td><StatusBadge status={r.status} /></Td>
                <Td>${r.totalAmount}</Td>
              </tr>
            ))}
          </tbody>
        </DataTable>
      ) : (
        <EmptyState icon={Search} title="Search reservations" description="Use the filters above to find reservations." />
      )}
    </div>
  );
}

// ─── Walk-in Modal ──────────────────────────────────────────────────────────

function showToast(message: string) {
  const el = document.createElement('div');
  el.setAttribute('role', 'status');
  el.setAttribute('aria-live', 'polite');
  el.className =
    'fixed bottom-4 right-4 z-[100] bg-green-600 text-white px-4 py-3 rounded-lg shadow-lg text-sm font-medium animate-slide-in';
  el.textContent = message;
  document.body.appendChild(el);
  setTimeout(() => {
    el.style.opacity = '0';
    el.style.transition = 'opacity 300ms';
    setTimeout(() => el.remove(), 300);
  }, 3000);
}

interface UnitOption {
  id: string;
  name: string;
  type: string;
  capacity: number;
  basePrice: string;
  housekeepingStatus: string;
  isActive: boolean;
}

function WalkInModal({
  isOpen,
  onClose,
  propertyId,
}: {
  isOpen: boolean;
  onClose: () => void;
  propertyId: string | undefined;
}) {
  const nameId = useId();
  const emailId = useId();
  const phoneId = useId();
  const unitId = useId();
  const checkInId = useId();
  const checkOutId = useId();
  const guestsCountId = useId();
  const rateId = useId();
  const notesId = useId();

  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  const [selectedUnit, setSelectedUnit] = useState('');
  const [checkIn, setCheckIn] = useState(() => format(new Date(), 'yyyy-MM-dd'));
  const [checkOut, setCheckOut] = useState(() => format(addDays(new Date(), 1), 'yyyy-MM-dd'));
  const [guestsCount, setGuestsCount] = useState(1);
  const [rate, setRate] = useState('');
  const [notes, setNotes] = useState('');

  const unitsQuery = useUnits(propertyId);
  const walkInMutation = useCreateWalkIn();

  // Show all active units — let the user decide. Mark status for clarity.
  const availableUnits: UnitOption[] = useMemo(() => {
    const all: UnitOption[] = unitsQuery.data ?? [];
    return all.filter((u) => u.isActive);
  }, [unitsQuery.data]);

  // Auto-populate rate when unit is selected
  const selectedUnitData = useMemo(
    () => availableUnits.find((u) => u.id === selectedUnit),
    [availableUnits, selectedUnit],
  );

  const handleUnitChange = (id: string) => {
    setSelectedUnit(id);
    const unit = availableUnits.find((u) => u.id === id);
    if (unit) {
      setRate(unit.basePrice);
    }
  };

  const resetForm = () => {
    setGuestName('');
    setGuestEmail('');
    setGuestPhone('');
    setSelectedUnit('');
    setCheckIn(format(new Date(), 'yyyy-MM-dd'));
    setCheckOut(format(addDays(new Date(), 1), 'yyyy-MM-dd'));
    setGuestsCount(1);
    setRate('');
    setNotes('');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUnit || !guestName.trim()) return;

    const todayStr = format(new Date(), 'yyyy-MM-dd');
    walkInMutation.mutate(
      {
        unitId: selectedUnit,
        guestData: {
          name: guestName.trim(),
          ...(guestEmail.trim() ? { email: guestEmail.trim() } : {}),
          ...(guestPhone.trim() ? { phone: guestPhone.trim() } : {}),
        },
        // Only send checkIn if it's not today — the backend defaults to today.
        // This keeps the payload compatible with the old DTO (forbidNonWhitelisted).
        ...(checkIn !== todayStr ? { checkIn } : {}),
        checkOut,
        guestsCount,
        rate: parseFloat(rate) || 0,
        ...(notes.trim() ? { notes: notes.trim() } : {}),
      },
      {
        onSuccess: () => {
          showToast('Walk-in guest checked in successfully.');
          resetForm();
          onClose();
        },
      },
    );
  };

  const today = format(new Date(), 'yyyy-MM-dd');
  const isValid = selectedUnit && guestName.trim() && checkIn && checkOut > checkIn && parseFloat(rate) >= 0;

  return (
    <SlideOver isOpen={isOpen} onClose={onClose} title="Walk-in Check-in">
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Unit selector */}
        <div>
          <label htmlFor={unitId} className="block text-sm font-medium text-gray-700 mb-1">
            Unit <span aria-label="required" className="text-red-500">*</span>
          </label>
          <select
            id={unitId}
            value={selectedUnit}
            onChange={(e) => handleUnitChange(e.target.value)}
            required
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">Select an available unit</option>
            {availableUnits.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name} — {u.type} (max {u.capacity}) [{u.housekeepingStatus}]
              </option>
            ))}
          </select>
          {unitsQuery.isLoading && (
            <p className="text-xs text-gray-400 mt-1">Loading units...</p>
          )}
          {!unitsQuery.isLoading && availableUnits.length === 0 && (
            <p className="text-xs text-amber-600 mt-1">No active units found for this property.</p>
          )}
        </div>

        {/* Guest name */}
        <div>
          <label htmlFor={nameId} className="block text-sm font-medium text-gray-700 mb-1">
            Guest Name <span aria-label="required" className="text-red-500">*</span>
          </label>
          <input
            id={nameId}
            type="text"
            value={guestName}
            onChange={(e) => setGuestName(e.target.value)}
            required
            placeholder="Full name"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        {/* Email & Phone row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor={emailId} className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              id={emailId}
              type="email"
              value={guestEmail}
              onChange={(e) => setGuestEmail(e.target.value)}
              placeholder="guest@example.com"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label htmlFor={phoneId} className="block text-sm font-medium text-gray-700 mb-1">
              Phone
            </label>
            <input
              id={phoneId}
              type="tel"
              value={guestPhone}
              onChange={(e) => setGuestPhone(e.target.value)}
              placeholder="+44 ..."
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>

        {/* Check-in & Check-out date row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor={checkInId} className="block text-sm font-medium text-gray-700 mb-1">
              Check-in Date <span aria-label="required" className="text-red-500">*</span>
            </label>
            <input
              id={checkInId}
              type="date"
              value={checkIn}
              min={today}
              onChange={(e) => {
                setCheckIn(e.target.value);
                if (checkOut <= e.target.value) {
                  setCheckOut(format(addDays(new Date(e.target.value), 1), 'yyyy-MM-dd'));
                }
              }}
              required
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label htmlFor={checkOutId} className="block text-sm font-medium text-gray-700 mb-1">
              Check-out Date <span aria-label="required" className="text-red-500">*</span>
            </label>
            <input
              id={checkOutId}
              type="date"
              value={checkOut}
              min={checkIn ? format(addDays(new Date(checkIn), 1), 'yyyy-MM-dd') : format(addDays(new Date(), 1), 'yyyy-MM-dd')}
              onChange={(e) => setCheckOut(e.target.value)}
              required
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>

        {/* Guests count */}
        <div>
          <label htmlFor={guestsCountId} className="block text-sm font-medium text-gray-700 mb-1">
            Guests
          </label>
          <input
            id={guestsCountId}
            type="number"
            value={guestsCount}
            min={1}
            max={selectedUnitData?.capacity ?? 20}
            onChange={(e) => setGuestsCount(Math.max(1, parseInt(e.target.value, 10) || 1))}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        {/* Nightly rate */}
        <div>
          <label htmlFor={rateId} className="block text-sm font-medium text-gray-700 mb-1">
            Nightly Rate <span aria-label="required" className="text-red-500">*</span>
          </label>
          <input
            id={rateId}
            type="number"
            value={rate}
            min={0}
            step="0.01"
            onChange={(e) => setRate(e.target.value)}
            required
            placeholder="0.00"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        {/* Special requests / notes */}
        <div>
          <label htmlFor={notesId} className="block text-sm font-medium text-gray-700 mb-1">
            Notes / Special Requests
          </label>
          <textarea
            id={notesId}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder="Any special requests..."
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
          />
        </div>

        {/* Error message */}
        {walkInMutation.isError && (
          <p role="alert" className="text-sm text-red-600">
            {(walkInMutation.error as Error)?.message ?? 'Failed to create walk-in booking. Please try again.'}
          </p>
        )}

        {/* Submit */}
        <div className="pt-2">
          <Button
            type="submit"
            fullWidth
            isLoading={walkInMutation.isPending}
            disabled={!isValid}
          >
            <LogIn size={16} aria-hidden="true" />
            Check In Now
          </Button>
        </div>
      </form>
    </SlideOver>
  );
}

// ─── Corporate Booking Modal ─────────────────────────────────────────────────

function CorporateBookingModal({
  isOpen,
  onClose,
  propertyId,
}: {
  isOpen: boolean;
  onClose: () => void;
  propertyId: string | undefined;
}) {
  const unitSelectId = useId();
  const companySelectId = useId();
  const nameId = useId();
  const emailId = useId();
  const phoneId = useId();
  const corpCheckInId = useId();
  const corpCheckOutId = useId();
  const guestsId = useId();
  const rateId = useId();
  const refId = useId();
  const notesId = useId();

  const today = format(new Date(), 'yyyy-MM-dd');

  const [selectedUnit, setSelectedUnit] = useState('');
  const [selectedCompany, setSelectedCompany] = useState('');
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [guestPhone, setGuestPhone] = useState('');
  const [checkIn, setCheckIn] = useState(today);
  const [checkOut, setCheckOut] = useState(() => format(addDays(new Date(), 1), 'yyyy-MM-dd'));
  const [guestsCount, setGuestsCount] = useState(1);
  const [rate, setRate] = useState('');
  const [corpRef, setCorpRef] = useState('');
  const [notes, setNotes] = useState('');

  const unitsQuery = useUnits(propertyId);
  const companiesQuery = useCompanies();
  const walkInMutation = useCreateWalkIn();

  const units = useMemo(() => (unitsQuery.data ?? []).filter((u: { isActive: boolean }) => u.isActive), [unitsQuery.data]);
  const companies = useMemo(() => {
    const data = companiesQuery.data as { data?: Array<{ id: string; name: string }> } | Array<{ id: string; name: string }> | undefined;
    return Array.isArray(data) ? data : (data?.data ?? []);
  }, [companiesQuery.data]);

  const selectedUnitData = useMemo(() => units.find((u: { id: string }) => u.id === selectedUnit), [units, selectedUnit]);

  const handleUnitChange = (id: string) => {
    setSelectedUnit(id);
    const unit = units.find((u: { id: string; basePrice: string }) => u.id === id);
    if (unit) setRate((unit as { basePrice: string }).basePrice);
  };

  const reset = () => {
    setSelectedUnit(''); setSelectedCompany(''); setGuestName(''); setGuestEmail('');
    setGuestPhone(''); setCheckIn(today); setCheckOut(format(addDays(new Date(), 1), 'yyyy-MM-dd'));
    setGuestsCount(1); setRate(''); setCorpRef(''); setNotes('');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUnit || !guestName.trim() || !selectedCompany) return;

    const companyName = companies.find((c) => c.id === selectedCompany)?.name ?? selectedCompany;
    const fullNotes = [
      `[Corporate: ${companyName}]`,
      corpRef.trim() ? `Ref: ${corpRef.trim()}` : '',
      notes.trim(),
    ].filter(Boolean).join(' | ');

    const todayStr = format(new Date(), 'yyyy-MM-dd');
    walkInMutation.mutate(
      {
        unitId: selectedUnit,
        guestData: {
          name: guestName.trim(),
          ...(guestEmail.trim() ? { email: guestEmail.trim() } : {}),
          ...(guestPhone.trim() ? { phone: guestPhone.trim() } : {}),
        },
        ...(checkIn !== todayStr ? { checkIn } : {}),
        checkOut,
        guestsCount,
        rate: parseFloat(rate) || 0,
        notes: fullNotes,
      },
      {
        onSuccess: () => {
          showToast('Corporate booking created successfully.');
          reset();
          onClose();
        },
      },
    );
  };

  const isValid = selectedUnit && selectedCompany && guestName.trim() && checkIn && checkOut > checkIn && parseFloat(rate) >= 0;

  return (
    <SlideOver isOpen={isOpen} onClose={onClose} title="Corporate Booking">
      <form onSubmit={handleSubmit} className="space-y-5">

        {/* Company selector */}
        <div>
          <label htmlFor={companySelectId} className="block text-sm font-medium text-gray-700 mb-1">
            Company <span className="text-red-500">*</span>
          </label>
          <select
            id={companySelectId}
            value={selectedCompany}
            onChange={(e) => setSelectedCompany(e.target.value)}
            required
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">Select a company...</option>
            {companies.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          {companiesQuery.isLoading && <p className="text-xs text-gray-400 mt-1">Loading companies...</p>}
          {!companiesQuery.isLoading && companies.length === 0 && (
            <p className="text-xs text-amber-600 mt-1">No companies found. Add one in the Companies page first.</p>
          )}
        </div>

        {/* Corporate reference */}
        <div>
          <label htmlFor={refId} className="block text-sm font-medium text-gray-700 mb-1">
            Corporate Reference / PO Number
          </label>
          <input
            id={refId}
            type="text"
            value={corpRef}
            onChange={(e) => setCorpRef(e.target.value)}
            placeholder="e.g. PO-2026-1234"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        {/* Unit */}
        <div>
          <label htmlFor={unitSelectId} className="block text-sm font-medium text-gray-700 mb-1">
            Unit <span className="text-red-500">*</span>
          </label>
          <select
            id={unitSelectId}
            value={selectedUnit}
            onChange={(e) => handleUnitChange(e.target.value)}
            required
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">Select an available unit</option>
            {units.map((u: { id: string; name: string; type: string; capacity: number; housekeepingStatus: string }) => (
              <option key={u.id} value={u.id}>{u.name} — {u.type} (max {u.capacity}) [{u.housekeepingStatus}]</option>
            ))}
          </select>
        </div>

        {/* Guest name */}
        <div>
          <label htmlFor={nameId} className="block text-sm font-medium text-gray-700 mb-1">
            Guest Name <span className="text-red-500">*</span>
          </label>
          <input id={nameId} type="text" value={guestName} onChange={(e) => setGuestName(e.target.value)} required placeholder="Full name"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
        </div>

        {/* Email & Phone */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor={emailId} className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input id={emailId} type="email" value={guestEmail} onChange={(e) => setGuestEmail(e.target.value)} placeholder="guest@company.com"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
          </div>
          <div>
            <label htmlFor={phoneId} className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
            <input id={phoneId} type="tel" value={guestPhone} onChange={(e) => setGuestPhone(e.target.value)} placeholder="+44 ..."
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
          </div>
        </div>

        {/* Check-in & Check-out */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor={corpCheckInId} className="block text-sm font-medium text-gray-700 mb-1">
              Check-in Date <span className="text-red-500">*</span>
            </label>
            <input id={corpCheckInId} type="date" value={checkIn} min={today}
              onChange={(e) => {
                setCheckIn(e.target.value);
                if (checkOut <= e.target.value) setCheckOut(format(addDays(new Date(e.target.value), 1), 'yyyy-MM-dd'));
              }}
              required className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
          </div>
          <div>
            <label htmlFor={corpCheckOutId} className="block text-sm font-medium text-gray-700 mb-1">
              Check-out Date <span className="text-red-500">*</span>
            </label>
            <input id={corpCheckOutId} type="date" value={checkOut}
              min={checkIn ? format(addDays(new Date(checkIn), 1), 'yyyy-MM-dd') : format(addDays(new Date(), 1), 'yyyy-MM-dd')}
              onChange={(e) => setCheckOut(e.target.value)} required
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
          </div>
        </div>

        {/* Guests & Rate */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor={guestsId} className="block text-sm font-medium text-gray-700 mb-1">Guests</label>
            <input id={guestsId} type="number" value={guestsCount} min={1} max={(selectedUnitData as { capacity?: number } | undefined)?.capacity ?? 20}
              onChange={(e) => setGuestsCount(Math.max(1, parseInt(e.target.value, 10) || 1))}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
          </div>
          <div>
            <label htmlFor={rateId} className="block text-sm font-medium text-gray-700 mb-1">
              Nightly Rate <span className="text-red-500">*</span>
            </label>
            <input id={rateId} type="number" value={rate} min={0} step="0.01" onChange={(e) => setRate(e.target.value)} required placeholder="0.00"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
          </div>
        </div>

        {/* Notes */}
        <div>
          <label htmlFor={notesId} className="block text-sm font-medium text-gray-700 mb-1">Notes / Special Requests</label>
          <textarea id={notesId} value={notes} onChange={(e) => setNotes(e.target.value)} rows={3}
            placeholder="Any special requirements for this corporate guest..."
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none" />
        </div>

        {walkInMutation.isError && (
          <p role="alert" className="text-sm text-red-600">
            {(walkInMutation.error as Error)?.message ?? 'Failed to create corporate booking.'}
          </p>
        )}

        <div className="pt-2">
          <Button type="submit" fullWidth isLoading={walkInMutation.isPending} disabled={!isValid}>
            <Building size={16} aria-hidden="true" />
            Create Corporate Booking
          </Button>
        </div>
      </form>
    </SlideOver>
  );
}

// ─── Shared sub-components ───────────────────────────────────────────────────

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center py-20">
      <Loader2 size={32} className="animate-spin text-primary-500" aria-label="Loading" />
    </div>
  );
}

function SummaryCard({ label, value, color }: { label: string; value: string | number; color: string }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4" aria-label={`${label}: ${value}`}>
      <p className={cn('text-2xl font-bold', color)}>{value}</p>
      <p className="text-sm text-gray-500 mt-1">{label}</p>
    </div>
  );
}

function DataTable({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          {children}
        </table>
      </div>
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50">
      {children}
    </th>
  );
}

function Td({ children }: { children: React.ReactNode }) {
  return (
    <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
      {children}
    </td>
  );
}
